import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Grid,
    CardContent,
    Card,
    Skeleton,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import FormData from 'form-data';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CheckIcon from '@mui/icons-material/Check';
import { styles } from './styles';
import { usePreModifyReviewPolicyMutation, useModifyReviewPolicyMutation } from '../../../../api/preHQ';

const ModifyCRPolicies = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [isTouched, setTouched] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const [preModifyChangeRequestPolicy, { data, isLoading: isPreModifyLoading }] = usePreModifyReviewPolicyMutation();
    const [modifyChangeRequestPolicy, { isLoading: isModifyLoading }] = useModifyReviewPolicyMutation();

    const { availableStatuses = [], policy: changeRequestPolicy = {}, approvers = [], reviewers = [] } = data || {};

    const [policy, setPolicy] = useState({
        name: changeRequestPolicy?.name,
        status: changeRequestPolicy?.status,
        reviewerId: changeRequestPolicy?.reviewerId,
        approverId: changeRequestPolicy?.approverId,
    });

    useEffect(() => {
        setPolicy({
            name: changeRequestPolicy?.name,
            status: changeRequestPolicy?.status,
            reviewerId: changeRequestPolicy?.reviewer,
            approverId: changeRequestPolicy?.approver,
        });
    }, [changeRequestPolicy]);

    useEffect(() => {
        const data = new FormData();
        data.append('id', Number(params?.id));
        try {
            preModifyChangeRequestPolicy(data).unwrap();
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleUpdate = async () => {
        const { name, status } = policy;

        if (!!name && !!status) {
            const payload = {
                id: params?.id,
                name,
                status,
            };
            payload['reviewerId'] = policy.reviewerId;
            payload['approverId'] = policy.approverId;
            try {
                const response = await modifyChangeRequestPolicy(payload).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                enqueueSnackbar(response?.message, { variant: 'success' });
                navigate('/client/reviewrequest/reviewpolicies');
            } catch (error) {
                if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
                else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
            }
        }
    };

    const handleChange = (data, type) => {
        if (type === 'Reviewer') setPolicy({ ...policy, reviewerId: data });
        if (type === 'Approver') setPolicy({ ...policy, approverId: data });
    };

    const handleBack = () => {
        navigate(-1);
    };

    const isDisabled = useMemo(
        () => !policy.reviewerId || !policy.approverId || !policy.status || !policy.name,
        [policy],
    );

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Edit Review Policy</Typography>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <Grid container direction="column" spacing={2}>
                            <Grid
                                item
                                sx={{
                                    display: 'flex',
                                }}
                            >
                                <Button
                                    sx={styles.backButton}
                                    startIcon={<ChevronLeftIcon fontSize="small" />}
                                    onClick={handleBack}
                                >
                                    Back
                                </Button>
                            </Grid>
                        </Grid>
                        {isPreModifyLoading ? (
                            <Box
                                sx={{
                                    py: 4,
                                }}
                            >
                                <Skeleton height={42} />
                                <Skeleton />
                                <Skeleton />
                            </Box>
                        ) : (
                            <Grid item xs={12}>
                                <Box sx={styles.basicInformation} component="form" noValidate autoComplete="off">
                                    <Box sx={styles.inputBox}>
                                        <TextField
                                            required
                                            error={policy.nameError}
                                            variant="outlined"
                                            id="outlined-name"
                                            label="Name"
                                            sx={styles.inputs}
                                            size="small"
                                            value={policy.name ?? ' '}
                                            onChange={(e) => {
                                                if (!isTouched) setTouched(true);
                                                setPolicy({
                                                    ...policy,
                                                    name: e.target.value,
                                                    nameError: false,
                                                });
                                            }}
                                        />
                                        <FormControl sx={styles.inputs} size="small" error={policy.statusError}>
                                            <InputLabel required id="demo-simple-select-label">
                                                Available Status
                                            </InputLabel>
                                            <Select
                                                labelId="demo-simple-select-label"
                                                id="demo-simple-select"
                                                label="Available Statuses"
                                                value={policy.status ?? ''}
                                                onChange={(e) => {
                                                    if (!isTouched) setTouched(true);
                                                    setPolicy({
                                                        ...policy,
                                                        status: e.target.value,
                                                        statusError: false,
                                                    });
                                                }}
                                            >
                                                {availableStatuses.map((item, index) => (
                                                    <MenuItem value={item} key={index}>
                                                        {item}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Box>
                                <Box sx={styles.bottomBLine}></Box>

                                <Box sx={styles.additionalPreOfferButton}>
                                    <Typography variant="h6" sx={styles.basicInformationText}>
                                        Policy Reviewer
                                    </Typography>
                                </Box>
                                <Box sx={styles.employeeListsBox}>
                                    <Box sx={styles.inputsAdditionalPreOfferBox}>
                                        <FormControl sx={styles.inputsAdditionalPreOffer} size="small">
                                            <InputLabel id="demo-simple-select-label">Reviewer</InputLabel>
                                            <Select
                                                labelId="demo-simple-select-label"
                                                id="demo-simple-select"
                                                defaultValue=""
                                                value={policy.reviewerId || ''}
                                                label={'Reviewer'}
                                                onChange={(e) => handleChange(e.target.value, 'Reviewer')}
                                            >
                                                {reviewers?.map((val) => (
                                                    <MenuItem key={val.id} value={val.id}>
                                                        {val.fullNameWithRole}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Box>
                                <Box sx={styles.bottomBLine}></Box>

                                <Box sx={styles.additionalPreOfferButton}>
                                    <Typography variant="h6" sx={styles.basicInformationText}>
                                        Policy Approver
                                    </Typography>
                                </Box>
                                <Box sx={styles.employeeListsBox}>
                                    <Box sx={styles.inputsAdditionalPreOfferBox}>
                                        <FormControl sx={styles.inputsAdditionalPreOffer} size="small">
                                            <InputLabel id="demo-simple-select-label">Approver</InputLabel>
                                            <Select
                                                labelId="demo-simple-select-label"
                                                id="demo-simple-select"
                                                defaultValue=""
                                                value={policy.approverId || ''}
                                                label={'Approver'}
                                                onChange={(e) => handleChange(e.target.value, 'Approver')}
                                            >
                                                {approvers.map((val) => (
                                                    <MenuItem key={val.id} value={val.id}>
                                                        {val.fullNameWithRole}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Box>
                                <Grid
                                    item
                                    sx={{
                                        pt: 2,
                                    }}
                                    xs={12}
                                >
                                    <Box>
                                        <Box sx={styles.bottomBLine}></Box>
                                        <Box sx={styles.createButtonBox}>
                                            <Button
                                                disabled={isDisabled || isModifyLoading}
                                                color="primary"
                                                onClick={handleUpdate}
                                                size="large"
                                                sx={styles.createButton}
                                                variant="contained"
                                                startIcon={<CheckIcon fontSize="small" />}
                                            >
                                                Update
                                            </Button>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default ModifyCRPolicies;
